import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  return (
    <div className="bg-white">

      {/* Hero Header */}
      <section className="relative overflow-hidden bg-neutral-50">
        <div className="section-container py-16 sm:py-20 lg:py-28">
          <nav className="flex items-center space-x-2 text-sm text-neutral-400 mb-10">
            <Link to="/" className="hover:text-forest-600 transition-colors">Home</Link>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-neutral-950 font-medium">Terms of Service</span>
          </nav>
          <h1 className="font-display text-display-sm sm:text-display-md lg:text-display-lg text-neutral-950 tracking-tight mb-4">
            Terms of Service
          </h1>
          <p className="text-body-lg text-neutral-500 max-w-2xl">
            Last updated: March 1, 2026. Please read these terms carefully before using our services.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-container py-16 lg:py-24">
        <div className="max-w-3xl mx-auto">

          {/* 1. Acceptance of Terms */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">1. Acceptance of Terms</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                By accessing or using the LiberaCure website, mobile applications, and related services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to all of these Terms, you may not access or use our Services.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you ("Customer," "you," or "your") and LiberaCure ("Company," "we," "us," or "our"). Your continued use of the Services following the posting of any changes to these Terms constitutes acceptance of those changes.
              </p>
              <p>
                You represent that you are at least 18 years of age (or the age of legal majority in your jurisdiction) and are fully competent to enter into the terms, conditions, obligations, representations, and warranties set forth in these Terms.
              </p>
            </div>
          </div>

          {/* 2. Services Description */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">2. Description of Services</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                LiberaCure is an international online pharmacy that provides access to generic and branded pharmaceutical products for personal use. Our Services include, but are not limited to:
              </p>
              <ul className="list-disc list-outside ml-6 space-y-2">
                <li>Retail sale of over-the-counter and prescription-equivalent generic medications</li>
                <li>Health and wellness product information and educational resources</li>
                <li>Customer support, order tracking, and pharmacy consultation services</li>
                <li>International shipping and fulfillment of pharmaceutical orders</li>
              </ul>
              <p>
                All products offered through LiberaCure are sourced from licensed manufacturers and comply with applicable pharmaceutical manufacturing standards, including Good Manufacturing Practice (GMP) regulations.
              </p>
              <p>
                The information provided on our website is for general informational purposes only and does not constitute medical advice. Always consult a qualified healthcare provider before starting, changing, or discontinuing any medication.
              </p>
            </div>
          </div>

          {/* 3. Ordering and Account */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">3. Ordering and Your Account</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                To place an order through our Services, you may be required to create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
              </p>
              <p>
                When placing an order, you agree to provide accurate, current, and complete information including your full name, shipping address, and contact details. Providing false or misleading information may result in order cancellation and account suspension.
              </p>
              <p>
                All orders are subject to acceptance and availability. We reserve the right to refuse or cancel any order at our sole discretion, including but not limited to cases where: pricing errors are identified, products are unavailable, we suspect fraudulent activity, or the order violates applicable laws.
              </p>
              <p>
                By placing an order for pharmaceutical products, you confirm that the products are intended for your personal use (or the personal use of someone for whom you are legally authorized to order) and that you are aware of the intended use, dosage, and potential side effects of the medication.
              </p>
            </div>
          </div>

          {/* 4. Personal Import Rights */}
          <div className="mb-14">
            <div className="bg-forest-50/50 border border-forest-100 rounded-2xl p-6 sm:p-8">
              <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">4. Personal Import Rights</h2>
              <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
                <p>
                  Many countries recognize the right of individuals to import medications for personal use. This right is generally based on the principle that patients should have access to affordable medications necessary for their health, even when such products are not marketed locally or are prohibitively expensive in their home country.
                </p>
                <p>
                  In the United States, the Food and Drug Administration (FDA) exercises enforcement discretion under its personal importation policy, generally permitting individuals to import up to a 90-day supply of medication for personal use, provided the drug is not considered to pose an unreasonable safety risk, the individual affirms in writing that the product is for personal use, and the drug is not commercially available domestically or is being used for a serious condition for which there is no effective domestic treatment.
                </p>
                <p>
                  In the United Kingdom, individuals may import medicines for personal use under the provisions of the Medicines Act 1968 and related guidance from the Medicines and Healthcare products Regulatory Agency (MHRA). The European Union similarly allows member states to permit personal importation of medications within reasonable quantities for individual therapeutic use.
                </p>
                <p>
                  In Canada, Health Canada's personal importation policy permits individuals to import up to a 90-day supply of a prescription drug for personal use, subject to certain conditions. Australia's Therapeutic Goods Administration (TGA) allows personal importation of up to a 3-month supply of most medications through the Personal Importation Scheme.
                </p>
                <p>
                  <strong className="text-neutral-950">Customer Responsibility:</strong> It is your sole responsibility to understand and comply with the importation laws and regulations of your country, state, or jurisdiction. LiberaCure provides products for personal use only and does not guarantee that any specific product may be legally imported into your jurisdiction. By placing an order, you acknowledge that you have reviewed the applicable import regulations and accept full responsibility for ensuring compliance with local laws.
                </p>
                <p>
                  LiberaCure shall not be held liable for any products seized, delayed, or destroyed by customs authorities. In such cases, refunds or re-shipments will be handled in accordance with our Shipping and Returns policy.
                </p>
              </div>
            </div>
          </div>

          {/* 5. Payment */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">5. Payment</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                All prices displayed on our website are in United States Dollars (USD) unless otherwise indicated. Prices are subject to change without notice, but changes will not affect orders that have already been confirmed.
              </p>
              <p>
                We accept payment via major credit cards, debit cards, cryptocurrency, and other payment methods as indicated at checkout. Payment is processed at the time of order confirmation. By providing payment information, you represent and warrant that you are authorized to use the payment method.
              </p>
              <p>
                You are responsible for any applicable taxes, duties, or customs charges that may be imposed by your local jurisdiction upon importation of goods. These charges, if any, are not included in the product price or shipping cost and are your sole responsibility.
              </p>
            </div>
          </div>

          {/* 6. Shipping and Delivery */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">6. Shipping and Delivery</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                LiberaCure ships to most countries worldwide. Estimated delivery times are provided at checkout and vary by destination and shipping method. While we make every reasonable effort to deliver orders within the estimated timeframe, delivery dates are not guaranteed.
              </p>
              <p>
                All shipments are packaged in discreet, unmarked packaging with no indication of the contents or the nature of our business. We respect your privacy and take every precaution to ensure confidential delivery.
              </p>
              <p>
                Risk of loss and title for items purchased from LiberaCure pass to you upon delivery of the items to the carrier. We are not responsible for delays, losses, or damages caused by the shipping carrier, customs authorities, or other events beyond our control.
              </p>
              <p>
                In the event that a shipment is lost, damaged, or seized by customs, please contact our customer support team within 30 days of the expected delivery date. We will work with you to arrange a re-shipment or refund in accordance with our returns policy.
              </p>
            </div>
          </div>

          {/* 7. Returns and Refunds */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">7. Returns and Refunds</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                Due to the nature of pharmaceutical products and health regulations, we generally cannot accept returns of medications once they have been shipped. However, we are committed to customer satisfaction and will consider refund or re-shipment requests in the following circumstances:
              </p>
              <ul className="list-disc list-outside ml-6 space-y-2">
                <li>The product received is damaged, defective, or materially different from what was ordered</li>
                <li>The shipment was lost in transit and has not been delivered within a reasonable period</li>
                <li>The shipment was seized or returned by customs authorities</li>
                <li>An order was cancelled before it was dispatched from our fulfillment center</li>
              </ul>
              <p>
                Refund requests must be submitted within 30 days of the original order date by contacting our customer support team. Approved refunds will be processed to the original payment method within 10 business days.
              </p>
              <p>
                LiberaCure reserves the right to deny refund requests that it reasonably believes to be fraudulent, excessive, or not in good faith.
              </p>
            </div>
          </div>

          {/* 8. Limitation of Liability */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">8. Limitation of Liability</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                To the maximum extent permitted by applicable law, LiberaCure and its officers, directors, employees, agents, suppliers, and licensors shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, goodwill, or other intangible losses, arising out of or related to your use of or inability to use the Services.
              </p>
              <p>
                Our total aggregate liability for all claims related to the Services shall not exceed the total amount paid by you to LiberaCure during the twelve (12) months immediately preceding the event giving rise to the claim.
              </p>
              <p>
                LiberaCure does not warrant that the Services will be uninterrupted, error-free, or free of viruses or other harmful components. The Services and all products are provided "as is" and "as available" without warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
              </p>
              <p>
                You acknowledge that LiberaCure is not a licensed pharmacy in your jurisdiction and that the products provided are for personal importation in accordance with applicable laws. You assume all risks associated with the purchase, importation, and use of pharmaceutical products obtained through our Services.
              </p>
            </div>
          </div>

          {/* 9. Intellectual Property */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">9. Intellectual Property</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                All content on the LiberaCure website, including text, graphics, logos, images, product descriptions, and software, is the property of LiberaCure or its content suppliers and is protected by international copyright, trademark, and other intellectual property laws.
              </p>
              <p>
                You may not reproduce, distribute, modify, create derivative works from, publicly display, or commercially exploit any content from our Services without our prior written consent.
              </p>
            </div>
          </div>

          {/* 10. Governing Law and Dispute Resolution */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">10. Governing Law and Dispute Resolution</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which LiberaCure is incorporated, without regard to its conflict of law provisions.
              </p>
              <p>
                Any dispute, controversy, or claim arising out of or relating to these Terms, or the breach, termination, or invalidity thereof, shall first be attempted to be resolved through good-faith negotiation. If the dispute cannot be resolved through negotiation within 30 days, either party may submit the dispute to binding arbitration in accordance with the rules of a mutually agreed-upon arbitral institution.
              </p>
              <p>
                Nothing in this section shall prevent either party from seeking injunctive or other equitable relief from a court of competent jurisdiction to prevent the actual or threatened infringement of intellectual property rights or other irreparable harm.
              </p>
            </div>
          </div>

          {/* 11. Changes to Terms */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">11. Changes to These Terms</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                We reserve the right to update or modify these Terms at any time at our sole discretion. When we make material changes, we will update the "Last updated" date at the top of this page and, where appropriate, provide additional notice via email or a prominent notice on our website.
              </p>
              <p>
                Your continued use of the Services after any such changes constitutes your acceptance of the revised Terms. If you do not agree to the updated Terms, you must discontinue use of the Services.
              </p>
            </div>
          </div>

          {/* 12. Contact */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">12. Contact Information</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                If you have any questions or concerns regarding these Terms of Service, please contact us:
              </p>
              <div className="bg-neutral-50 rounded-2xl border border-neutral-150 p-6">
                <p className="font-medium text-neutral-950 mb-1">LiberaCure Customer Support</p>
                <p>Email: support@liberacure.com</p>
                <p>Response time: Within 24 hours on business days</p>
              </div>
            </div>
          </div>

          {/* Effective Date */}
          <div className="bg-neutral-50 rounded-2xl border border-neutral-150 p-6 text-center">
            <p className="text-body text-neutral-500">
              These Terms of Service are effective as of March 1, 2026.
            </p>
          </div>

        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
